<template>
  <section class="cw-flex cw-flex-col cw-overflow-y-hidden cw-h-full cw-border cw-border-gray-200"
    role="tabpanel">
    <!-- Filters -->
    <div ref="filterSection"
      class="cw-shadow-md cw-p-3">
      <country-selector :country-list="countryList"
        :selected-country="filterCountryValue"
        @updateFilter="handleUpdateFilter" />
      <locations-filter :filter-text-value="filterTextValue"
        :filter-options-value="filterOptionsValue"
        @updateFilter="handleUpdateFilter" />
    </div>

    <h2 class="cw-font-bold cw-px-3 cw-py-2 cw-mt-2">
      {{ $t('enabled-locations-map.locations') }}
    </h2>

    <!-- Locations Loading -->
    <div v-if="!loadingComplete"
      class="cw-border cw-border-gray-200">
      <span class="cw-sr-only">{{ $t('enabled-locations-map.loading-locations') }}</span>
      <location-item-skeleton />
      <location-item-skeleton />
      <location-item-skeleton />
      <location-item-skeleton />
      <location-item-skeleton />
      <location-item-skeleton />
    </div>
    <!-- No Locations -->
    <div v-else-if="locationsFilteredSorted.length === 0"
      class="cw-flex cw-justify-center cw-items-center cw-h-full cw-p-3 cw-text-gray-600">
      <p>{{ $t('enabled-locations-map.no-location-found') }}</p>
    </div>
    <!-- Locations Listing -->
    <div v-else
      ref="locationsListing"
      role="list"
      class="cw-overflow-y-scroll cw-border-t cw-border-gray-200">
      <location-item v-for="location in locationsFilteredSorted"
        :key="location.id"
        :location="location"
        :selected-location="selectedLocation"
        @selectLocation="handleSelectLocation">
        <!-- Mobile: Selected Location Map Snapshot -->
        <div v-if="location.id === selectedLocation.id && isMobileDevice"
          class="cw-h-[300px] cw-w-full cw-mb-5">
          <mapbox-static :mapbox-config="mapboxConfig"
            :selected-location="selectedLocation" />
        </div>
      </location-item>
    </div>
  </section>
</template>

<script>
import { get, debounce } from 'lodash'
import { LngLatBounds } from 'mapbox-gl'

import MapboxStatic from '@/locations-map/components/common/MapboxStatic'
import LocationItem from '@/locations-map/components/common/LocationItem'
import LocationItemSkeleton from '@/locations-map/components/locations/LocationItemSkeleton.vue'
import LocationsFilter from '@/locations-map/components/locations/LocationsFilter'
import CountrySelector from '@/locations-map/components/locations/CountrySelector'
import { dispatchCustomEvent, getBoundsFromPoints } from '@/locations-map/components/common/MapHelpers'

const LOCATION_SEARCH_FIELDS = ['name', 'dc.name', 'address.street', 'address.suburb', 'address.city', 'address.country']

export default {
  name: 'LocationsView',

  components: {
    'location-item': LocationItem,
    'location-item-skeleton': LocationItemSkeleton,
    'locations-filter': LocationsFilter,
    'country-selector': CountrySelector,
    'mapbox-static': MapboxStatic,
  },

  props: {
    enabledLocations: {
      type: Array,
      required: true,
    },
    locationsFilteredByFeature: {
      type: Array,
      required: true,
    },
    mapContext: {
      type: Object,
      required: true,
    },
    mapboxConfig: {
      type: Object,
      required: true,
    },
    isMobileDevice: {
      type: Boolean,
      required: false,
      default: false,
    },
    deepLinkLocation: {
      type: Object,
      required: true,
    },
    loadingComplete: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      filterTextValue: '',
      filterCountryValue: 'allCountries',
      filterOptionsValue: [],
      selectedLocation: {},
    }
  },

  computed: {
    countryList() {
      const countrySet = new Set()
      this.enabledLocations.forEach(location => {
        countrySet.add(location.country)
      })
      return Array.from(countrySet).sort()
    },

    locationsFilteredSorted() {
      let locations = this.locationsFilteredByFeature

      // Country filtering
      if (this.filterCountryValue !== 'allCountries') {
        locations = locations.filter(location => location.country.toLowerCase() === this.filterCountryValue)
      }
      // Search filtering
      if (this.filterTextValue) {
        const filterLowerCase = this.filterTextValue.toLocaleLowerCase()
        locations = locations.filter(location => LOCATION_SEARCH_FIELDS.some(field => {
          return get(location, field)?.toLocaleLowerCase().includes(filterLowerCase)
        }))
      }
      // Sorting
      locations.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

      return locations
    },
  },

  created() {
    this.debouncedZoomToSearch = debounce(this.zoomToSearch, 1000)
  },

  mounted() {
    window.addEventListener('mp_selectLocation', this.setSelectedLocation)
    this.$parent.$on('deepLinkLocationUpdated', this.handleSelectLocation)
  },

  destroyed() {
    window.removeEventListener('mp_selectLocation', this.setSelectedLocation)
    this.debouncedZoomToSearch.cancel()
    this.$parent.$off('deepLinkLocationUpdated', this.handleSelectLocation)
  },

  methods: {
    handleUpdateFilter(updateObj) {
      this[updateObj.key] = updateObj.value
      this.$emit('change', this.filterOptionsValue)
      if (this.filterTextValue) this.debouncedZoomToSearch()
    },
    setSelectedLocation(event) {
      this.selectedLocation = event.detail.location
    },
    async handleSelectLocation(location) {
      this.selectedLocation = location
      await this.$nextTick()
      this.dispatchMapEvents()
    },
    dispatchMapEvents() {
      dispatchCustomEvent('mp_showLocationPopup', { location: this.selectedLocation })
      dispatchCustomEvent('mp_zoomToLocation', {
        zoom: 13,
        center: [this.selectedLocation.longitude, this.selectedLocation.latitude],
      })
    },
    zoomToSearch() {
      if (this.locationsFilteredSorted.length < 1) return

      const locationCoordinates = this.locationsFilteredSorted.map(location => [location.longitude, location.latitude])
      const { south, west, north, east } = getBoundsFromPoints(locationCoordinates)
      // Only zoom if the bounds cover a small enough area
      if (east - west < 5 && north - south < 5) {
        dispatchCustomEvent('mp_zoomToBounds', {
          bounds: LngLatBounds.convert([south, west, north, east]),
          zoom: 15,
        })
      }
    },
  },
}
</script>
